import { memo, useCallback, useMemo, useState } from 'react'
import { announcementsApi, type ImportPreview } from '../../lib/infoserviceApi.js'
import { sheetDate, shortDayLabel } from '../../lib/announcementGrid.js'
import { ModalShell } from '../common/ModalShell.js'
import { Spinner } from '../common/Spinner.js'

interface SheetImportProps {
  channelId: string
  onImported: () => void
  onClose: () => void
}

type Filter = 'active' | 'archived' | 'warnings'

/**
 * Reprise unique de la feuille Google : Fichier → Télécharger → Microsoft
 * Excel (.xlsx). L'aperçu ne crée rien ; « Importer » relit le même fichier et
 * écrit tout en une transaction.
 */
export const SheetImportModal = memo(function SheetImportModal({ channelId, onImported, onClose }: SheetImportProps) {
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<ImportPreview | null>(null)
  const [busy, setBusy] = useState<'preview' | 'apply' | null>(null)
  const [error, setError] = useState('')
  const [filter, setFilter] = useState<Filter>('active')
  const [result, setResult] = useState<{ created: number; days: number; dayNotes: number } | null>(null)

  const choose = useCallback(async (picked: File | null) => {
    setFile(picked)
    setPreview(null)
    setError('')
    if (!picked) return
    setBusy('preview')
    try {
      setPreview(await announcementsApi.previewImport(channelId, picked))
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Lecture de la feuille impossible')
    } finally {
      setBusy(null)
    }
  }, [channelId])

  const apply = useCallback(async () => {
    if (!file) return
    setBusy('apply')
    setError('')
    try {
      setResult(await announcementsApi.applyImport(channelId, file))
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Import impossible')
    } finally {
      setBusy(null)
    }
  }, [channelId, file])

  const rows = useMemo(() => (preview?.rows ?? []).filter(row =>
    filter === 'warnings' ? row.warnings.length > 0 || row.cancelled || row.dayNotes.length > 0
      : filter === 'archived' ? row.archived : !row.archived), [filter, preview])

  return (
    <ModalShell title="Importer la feuille « Infographies »" size="wide" onClose={onClose} closeDisabled={busy === 'apply'} className="sheet-import">
      <div className="tp-modal__body sheet-import__body">
        {result ? (
          <div className="tp-feedback tp-feedback--success" role="status">
            {result.created} annonces importées, {result.days} jours cochés, {result.dayNotes} commentaire{result.dayNotes > 1 ? 's' : ''}.
            Les images restent à rattacher, depuis la fiche de chaque annonce.
          </div>
        ) : (
          <>
            <p className="announcement-drawer__hint">
              Dans Google Sheets : <strong>Fichier → Télécharger → Microsoft Excel (.xlsx)</strong>. L’export CSV ne convient
              pas : il perd les cases colorées qui portent les jours de diffusion. L’import est unique ; rien n’est créé avant
              de cliquer sur « Importer ».
            </p>
            <label className="tp-form-field">
              <span className="tp-field-label">Fichier .xlsx</span>
              <input
                className="tp-input"
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                disabled={busy !== null}
                onChange={event => void choose(event.target.files?.[0] ?? null)}
              />
            </label>
          </>
        )}

        {busy === 'preview' && <div className="tp-empty-state"><Spinner /> Lecture de la feuille…</div>}
        {error && <div className="tp-feedback tp-feedback--error" role="alert">{error}</div>}

        {preview && !result && (
          <>
            {preview.alreadyImported > 0 && (
              <div className="tp-feedback tp-feedback--warning" role="alert">
                Cette chaîne a déjà {preview.alreadyImported} annonces importées : un second import doublerait chaque ligne et sera refusé.
              </div>
            )}
            <dl className="sheet-import__summary">
              <div><dt>Feuille</dt><dd>{preview.sheetName}</dd></div>
              <div><dt>Période</dt><dd>{preview.firstDay && preview.lastDay ? `${sheetDate(preview.firstDay)} → ${sheetDate(preview.lastDay)}` : '—'}</dd></div>
              <div>
                <dt>Case cochée</dt>
                <dd>
                  <span className="sheet-import__swatch" style={{ '--swatch': `#${preview.checkedFill}` } as React.CSSProperties} aria-hidden="true" />
                  #{preview.checkedFill}
                </dd>
              </div>
              <div><dt>Annonces</dt><dd>{preview.counts.total} dont {preview.counts.active} en cours, {preview.counts.archived} archivées</dd></div>
              <div><dt>Jours cochés</dt><dd>{preview.counts.days}</dd></div>
              <div><dt>Commentaires</dt><dd>{preview.counts.dayNotes}</dd></div>
              <div><dt>Annonceurs reconnus</dt><dd>{preview.counts.advertisersMatched}</dd></div>
              <div><dt>Lignes ignorées</dt><dd>{preview.skippedRows.length ? preview.skippedRows.map(row => `ligne ${row.sheetRow}`).join(', ') : 'aucune'}</dd></div>
            </dl>

            <div className="tp-segmented" aria-label="Lignes affichées">
              <button type="button" className={`tp-segment${filter === 'active' ? ' is-active' : ''}`} aria-pressed={filter === 'active'} onClick={() => setFilter('active')}>En cours ({preview.counts.active})</button>
              <button type="button" className={`tp-segment${filter === 'archived' ? ' is-active' : ''}`} aria-pressed={filter === 'archived'} onClick={() => setFilter('archived')}>Archivées ({preview.counts.archived})</button>
              <button type="button" className={`tp-segment${filter === 'warnings' ? ' is-active' : ''}`} aria-pressed={filter === 'warnings'} onClick={() => setFilter('warnings')}>À vérifier</button>
            </div>

            <div className="sheet-import__table-wrap">
              <table className="sheet-import__table">
                <thead>
                  <tr>
                    <th scope="col">Ligne</th>
                    <th scope="col">Organisme</th>
                    <th scope="col">Événement</th>
                    <th scope="col">Jours</th>
                    <th scope="col">Remarques</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(row => (
                    <tr key={row.sheetRow} className={row.cancelled ? 'is-cancelled' : undefined}>
                      <td className="is-data">{row.sheetRow}</td>
                      <td>{row.organizationLabel}{row.advertiserName && <span className="sheet-import__advertiser"> · annonceur {row.advertiserName}</span>}</td>
                      <td>{row.eventLabel}</td>
                      <td className="is-data">
                        {row.days.length === 0 ? '—' : `${shortDayLabel(row.days[0])} → ${shortDayLabel(row.days[row.days.length - 1])} (${row.days.length})`}
                      </td>
                      <td>
                        {[
                          row.cancelled ? 'annulée' : null,
                          ...row.dayNotes.map(note => `${shortDayLabel(note.day)} : ${note.text}`),
                          ...row.warnings,
                        ].filter(Boolean).join(' · ')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
      <footer className="tp-modal__footer">
        {result ? (
          <button type="button" className="tp-button tp-button--primary" onClick={onImported}>Voir les annonces</button>
        ) : (
          <>
            <button type="button" className="tp-button" onClick={onClose} disabled={busy === 'apply'}>Annuler</button>
            <button
              type="button"
              className="tp-button tp-button--primary"
              disabled={!preview || busy !== null || preview.alreadyImported > 0}
              onClick={() => void apply()}
            >
              {busy === 'apply' && <Spinner />} Importer {preview ? `${preview.counts.total} annonces` : ''}
            </button>
          </>
        )}
      </footer>
    </ModalShell>
  )
})
